import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { ImageTextProps } from './ImageText.data';
import styles from './ImageText.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './ImageText.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Image from '../../atoms/image/Image';
import type { RichTextBlokData } from '../rich-text/RichText.data';
import type { StoryblokComponent } from 'storyblok-js-client';

import RichText from '../rich-text/RichText';

export default function ImageText({
  isVisible,
  className,
  blok,
  ref,
}: ImageTextProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.imageText, 'max-content-width', className)}
      {...sbEditable(blok)}
    >
      {blok.image?.filename && (
        <div
          className={classNames(styles.imageContainer, {
            [styles.right]: blok.image_position === 'right',
          })}
        >
          <Image
            src={blok.image.filename}
            srcDesktop={blok.image.filename}
            alt={blok.image.alt}
            className={classNames(styles.image, 'abs-fill')}
            layout="fill"
            objectFit="cover"
            innerTransition={{
              type: 'scaleUp',
            }}
          />
        </div>
      )}
      <div className={classNames(styles.textContent)}>
        <RichText
          blok={{ text: blok.text } as RichTextBlokData & StoryblokComponent<string>}
          className={styles.richText}
        />
      </div>
    </div>
  );
}
